import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { apiErrorMessage, formatPLN, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Check, X, Plus, Trash2, ArrowLeft, AlertTriangle, Edit3, Save, Truck, Package, Calendar, FileDown, Search, Trash } from "lucide-react";

const ADMIN_NEXT_STATUS = {
  zaakceptowane: { next: "w_realizacji", label: "Zaakceptuj i rozpocznij realizację", Icon: Truck },
  w_realizacji: { next: "zrealizowane", label: "Oznacz jako zrealizowane", Icon: Package },
};

const CHANGE_LABEL = {
  produkt_dodany: { label: "Dodano produkt", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  produkt_usuniety: { label: "Usunięto produkt", className: "bg-red-50 text-red-700 border-red-200" },
  ilosc_zmieniona: { label: "Zmieniono ilość", className: "bg-amber-50 text-amber-700 border-amber-200" },
};

export default function OrderDetail() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [changes, setChanges] = useState([]);
  const [products, setProducts] = useState([]);
  const [edit, setEdit] = useState(false);
  const [draftItems, setDraftItems] = useState([]);
  const [managerComment, setManagerComment] = useState("");
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [adminComment, setAdminComment] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [quoted, setQuoted] = useState([]);

  const reload = async () => {
    const o = await api.get(`/orders/${orderId}`);
    setOrder(o.data);
    setDraftItems(o.data.items.map((i) => ({ ...i })));
    setManagerComment(o.data.manager_comment || "");
    setAdminComment(o.data.office_land_comment || "");
    const c = await api.get(`/orders/${orderId}/changes`);
    setChanges(c.data);
  };

  useEffect(() => {
    reload();
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/custom-product-requests/quoted").then((r) => setQuoted(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const isMyOrder = order && user?.role === "manager"; // managers can edit pending
  const isAdmin = user?.role === "office_land_admin";
  const canApproveReject = isMyOrder && order?.status === "do_akceptacji";
  const canEdit = canApproveReject || isAdmin;
  const canAdminStatus = isAdmin && ADMIN_NEXT_STATUS[order?.status];

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products.slice(0, 50);
    return products.filter(
      (p) => `${p.name} ${p.sku} ${p.category || ""}`.toLowerCase().includes(q),
    ).slice(0, 50);
  }, [products, productSearch]);

  const filteredQuoted = useMemo(() => {
    if (!quoted.length) return [];
    const q = productSearch.trim().toLowerCase();
    if (!q) return quoted;
    return quoted.filter((qq) => qq.product_name.toLowerCase().includes(q));
  }, [quoted, productSearch]);

  const draftTotal = useMemo(
    () => draftItems.reduce((s, i) => s + Number(i.unit_price_net) * Number(i.quantity), 0),
    [draftItems],
  );

  if (!order) return <div className="text-sm text-slate-500">Ładowanie…</div>;

  const updateQty = (pid, q) => {
    setDraftItems((items) => items.map((i) => (i.product_id === pid ? { ...i, quantity: Math.max(0, q) } : i)).filter((i) => i.quantity > 0));
  };

  const removeFromDraft = (pid) => {
    setDraftItems((items) => items.filter((i) => i.product_id !== pid));
  };

  const itemsForPayload = () =>
    draftItems.map((i) => {
      const pid = String(i.product_id || "");
      if (pid.startsWith("custom:")) {
        return { custom_request_id: pid.slice("custom:".length), quantity: Number(i.quantity) };
      }
      return { product_id: i.product_id, quantity: Number(i.quantity) };
    });

  const saveEdit = async () => {
    try {
      if (isAdmin) {
        await api.patch(`/orders/${order.id}/admin-edit`, {
          items: itemsForPayload(),
          office_land_comment: adminComment || null,
        });
      } else {
        await api.patch(`/orders/${order.id}/edit`, {
          items: itemsForPayload(),
          manager_comment: managerComment || null,
        });
      }
      toast.success("Zapisano zmiany");
      setEdit(false);
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const deleteOrder = async () => {
    if (!isAdmin) return;
    if (!window.confirm(`Czy na pewno usunąć zamówienie ${order.order_number}? Tej operacji nie da się cofnąć.`)) return;
    try {
      await api.delete(`/orders/${order.id}`);
      toast.success("Zamówienie usunięte");
      navigate(-1);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const saveAdminComment = async () => {
    try {
      await api.patch(`/orders/${order.id}/admin-comment`, { office_land_comment: adminComment });
      toast.success("Komentarz zapisany");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const approve = async () => {
    try {
      await api.post(`/orders/${order.id}/approve`);
      toast.success("Zamówienie zaakceptowane");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const reject = async () => {
    try {
      await api.post(`/orders/${order.id}/reject`, { manager_comment: rejectComment || null });
      toast.success("Zamówienie odrzucone");
      setRejectMode(false);
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const adminStep = async () => {
    const cfg = ADMIN_NEXT_STATUS[order.status];
    if (!cfg) return;
    try {
      await api.patch(`/orders/${order.id}/status`, {
        status: cfg.next,
        delivery_date: deliveryDate || null,
        office_land_comment: adminComment || null,
      });
      toast.success(cfg.label);
      setDeliveryDate("");
      setAdminComment("");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/orders/${order.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zamowienie_${(order.order_number || "").replace(/\//g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Pobrano PDF");
    } catch (e) { toast.error(apiErrorMessage(e, "Nie udało się pobrać PDF")); }
  };

  const displayedItems = edit ? draftItems : order.items;
  const displayedTotal = edit ? draftTotal : order.final_total_net;

  return (
    <div>
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Wróć
      </button>

      <PageHeader
        testId="order-detail"
        title={`Zamówienie ${order.order_number}`}
        subtitle={`${order.site_name} · ${formatDate(order.created_at)}`}
        actions={
          <>
            <button onClick={downloadPdf} data-testid="download-pdf-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-sm font-semibold transition-colors">
              <FileDown className="w-4 h-4" /> Pobierz PDF
            </button>
            <StatusBadge status={order.status} />
          </>
        }
      />

      {order.budget_warning && (
        <div className="ol-card p-4 mb-6 border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="budget-warning-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">Zamówienie przekracza dostępny budżet miesięczny.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="ol-card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900">Pozycje zamówienia</h3>
              {canEdit && !edit && (
                <button onClick={() => setEdit(true)} data-testid="manager-edit-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-xs font-semibold">
                  <Edit3 className="w-3.5 h-3.5" /> Edytuj
                </button>
              )}
              {edit && (
                <div className="flex gap-2">
                  <button onClick={() => { setEdit(false); setDraftItems(order.items.map((i) => ({ ...i }))); }} className="px-3 py-1.5 rounded-md border border-slate-300 text-xs font-semibold">Anuluj</button>
                  <button onClick={saveEdit} data-testid="save-edit-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-xs font-semibold">
                    <Save className="w-3.5 h-3.5" /> Zapisz zmiany
                  </button>
                </div>
              )}
            </div>
            <table className="ol-table" data-testid="order-items-table">
              <thead>
                <tr><th>SKU</th><th>Produkt</th><th>Jedn.</th><th className="text-right">Cena</th><th className="text-center">Ilość</th><th className="text-right">Wartość</th>{edit && <th></th>}</tr>
              </thead>
              <tbody>
                {displayedItems.map((i) => (
                  <tr key={i.product_id}>
                    <td className="font-mono text-xs">{i.sku}</td>
                    <td className="font-medium">{i.product_name}</td>
                    <td className="text-slate-600">{i.unit}</td>
                    <td className="text-right">{formatPLN(i.unit_price_net)}</td>
                    <td className="text-center">
                      {edit ? (
                        <input type="number" min="0" value={i.quantity}
                          onChange={(e) => updateQty(i.product_id, Number(e.target.value))}
                          data-testid={`edit-qty-${i.sku}`}
                          className="w-20 text-center px-2 py-1 rounded border border-slate-300 text-sm" />
                      ) : i.quantity}
                    </td>
                    <td className="text-right font-semibold">{formatPLN(i.unit_price_net * i.quantity)}</td>
                    {edit && <td><button onClick={() => removeFromDraft(i.product_id)} data-testid={`del-${i.sku}`} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={edit ? 5 : 5} className="text-right font-semibold text-slate-700">Suma netto:</td>
                  <td className="text-right text-lg font-bold">{formatPLN(displayedTotal)}</td>
                  {edit && <td></td>}
                </tr>
              </tfoot>
            </table>

            {edit && (
              <div className="border-t border-slate-200 px-5 py-4 bg-slate-50">
                <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">Dodaj produkt z katalogu</p>
                <div className="relative mb-3">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Szukaj nazwy, SKU lub kategorii…"
                    data-testid="edit-product-search"
                    className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none bg-white"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100" data-testid="edit-product-results">
                  {filteredQuoted.length > 0 && (
                    <>
                      <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#CC4B00] bg-[#FFF4ED]">
                        Wyceny Office-Land (spoza katalogu)
                      </p>
                      {filteredQuoted.map((q) => {
                        const pid = `custom:${q.id}`;
                        return (
                          <div key={q.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-[#FFFBF7] hover:bg-[#FFF4ED]">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-900 truncate">
                                {q.product_name} <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF6B1A]/10 text-[#CC4B00] font-semibold uppercase">wycena</span>
                              </p>
                              <p className="text-[11px] text-slate-500">
                                {formatPLN(q.estimated_price_net)} / szt.
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                setDraftItems((its) => {
                                  const existing = its.find((i) => i.product_id === pid);
                                  if (existing) {
                                    return its.map((i) =>
                                      i.product_id === pid ? { ...i, quantity: i.quantity + 1 } : i,
                                    );
                                  }
                                  return [...its, {
                                    product_id: pid,
                                    product_name: q.product_name,
                                    sku: `CUSTOM-${q.id.slice(0, 8).toUpperCase()}`,
                                    unit: "szt.",
                                    quantity: 1,
                                    unit_price_net: q.estimated_price_net,
                                    line_total_net: q.estimated_price_net,
                                  }];
                                });
                              }}
                              data-testid={`edit-add-quoted-${q.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-xs font-semibold flex-shrink-0"
                            >
                              <Plus className="w-3.5 h-3.5" /> Dodaj
                            </button>
                          </div>
                        );
                      })}
                      <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 bg-slate-50">
                        Katalog
                      </p>
                    </>
                  )}
                  {filteredProducts.length === 0 && filteredQuoted.length === 0 ? (
                    <p className="p-4 text-xs text-slate-500 text-center">Brak wyników</p>
                  ) : (
                    filteredProducts.map((p) => (
                      <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-50">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 truncate">{p.name}</p>
                          <p className="text-[11px] text-slate-500 font-mono">{p.sku} · {formatPLN(p.price_net)}/{p.unit}</p>
                        </div>
                        <button
                          onClick={() => {
                            setDraftItems((items) => {
                              const existing = items.find((i) => i.product_id === p.id);
                              if (existing) {
                                return items.map((i) =>
                                  i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i,
                                );
                              }
                              return [...items, {
                                product_id: p.id,
                                product_name: p.name,
                                sku: p.sku,
                                unit: p.unit,
                                quantity: 1,
                                unit_price_net: p.price_net,
                                line_total_net: p.price_net,
                              }];
                            });
                          }}
                          data-testid={`edit-add-${p.sku}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-xs font-semibold flex-shrink-0"
                        >
                          <Plus className="w-3.5 h-3.5" /> Dodaj
                        </button>
                      </div>
                    ))
                  )}
                </div>
                {!isAdmin && (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wide">Komentarz kierownika</label>
                    <textarea rows={2} value={managerComment} onChange={(e) => setManagerComment(e.target.value)} data-testid="manager-comment"
                      className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
                  </div>
                )}
                {isAdmin && (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-slate-700 mb-1 uppercase tracking-wide">Komentarz Office-Land</label>
                    <textarea rows={2} value={adminComment} onChange={(e) => setAdminComment(e.target.value)} data-testid="admin-edit-comment"
                      className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
                  </div>
                )}
              </div>
            )}
          </div>

          {changes.length > 0 && (
            <div className="ol-card overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900">Zmiany wprowadzone przez kierownika</h3>
              </div>
              <table className="ol-table" data-testid="changes-table">
                <thead><tr><th>Typ</th><th>Produkt</th><th>Było</th><th>Jest</th><th>Komentarz</th><th>Kiedy</th></tr></thead>
                <tbody>
                  {changes.map((c, idx) => {
                    const cfg = CHANGE_LABEL[c.change_type] || { label: c.change_type, className: "bg-slate-100 text-slate-700" };
                    return (
                      <tr key={idx}>
                        <td><span className={`ol-badge border ${cfg.className}`}>{cfg.label}</span></td>
                        <td className="font-medium">{c.product_name}</td>
                        <td className="text-slate-600">{c.old_value}</td>
                        <td className="text-slate-900 font-semibold">{c.new_value}</td>
                        <td className="text-slate-600 text-sm">{c.note || "—"}</td>
                        <td className="text-xs text-slate-500">{formatDate(c.changed_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="ol-card p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Informacje</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Numer</dt><dd className="font-mono">{order.order_number}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Budowa</dt><dd className="text-right">{order.site_name}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Pracownik</dt><dd>{order.created_by_name}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Złożone</dt><dd>{formatDate(order.submitted_at)}</dd></div>
              {order.approved_at && <div className="flex justify-between"><dt className="text-slate-500">Zaakceptowane</dt><dd>{formatDate(order.approved_at)}</dd></div>}
              {order.rejected_at && <div className="flex justify-between"><dt className="text-slate-500">Odrzucone</dt><dd>{formatDate(order.rejected_at)}</dd></div>}
              {order.completed_at && <div className="flex justify-between"><dt className="text-slate-500">Zrealizowane</dt><dd>{formatDate(order.completed_at)}</dd></div>}
              {order.delivery_date && <div className="flex justify-between"><dt className="text-slate-500">Data dostawy</dt><dd className="font-semibold text-teal-700">{order.delivery_date}</dd></div>}
              <div className="flex justify-between"><dt className="text-slate-500">Wartość pierwotna</dt><dd>{formatPLN(order.original_total_net)}</dd></div>
              <div className="flex justify-between border-t border-slate-200 pt-2 mt-2"><dt className="font-semibold">Wartość końcowa</dt><dd className="font-bold text-lg">{formatPLN(order.final_total_net)}</dd></div>
            </dl>
          </div>

          {order.employee_comment && (
            <div className="ol-card p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Komentarz pracownika</p>
              <p className="text-sm text-slate-800">{order.employee_comment}</p>
            </div>
          )}
          {order.manager_comment && (
            <div className="ol-card p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Komentarz kierownika</p>
              <p className="text-sm text-slate-800">{order.manager_comment}</p>
            </div>
          )}
          {order.office_land_comment && (
            <div className="ol-card p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Komentarz Office-Land</p>
              <p className="text-sm text-slate-800">{order.office_land_comment}</p>
            </div>
          )}

          {canApproveReject && !edit && !rejectMode && (
            <div className="flex gap-2">
              <button onClick={approve} data-testid="approve-btn"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
                <Check className="w-4 h-4" /> Akceptuj
              </button>
              <button onClick={() => setRejectMode(true)} data-testid="reject-btn"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold">
                <X className="w-4 h-4" /> Odrzuć
              </button>
            </div>
          )}

          {rejectMode && (
            <div className="ol-card p-4 space-y-2">
              <textarea rows={3} value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} placeholder="Powód odrzucenia…" data-testid="reject-comment"
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
              <div className="flex gap-2">
                <button onClick={() => setRejectMode(false)} className="flex-1 px-3 py-2 rounded-md border border-slate-300 text-sm font-semibold">Anuluj</button>
                <button onClick={reject} data-testid="confirm-reject"
                  className="flex-1 px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">Potwierdź odrzucenie</button>
              </div>
            </div>
          )}

          {canAdminStatus && (() => {
            const AdminIcon = ADMIN_NEXT_STATUS[order.status].Icon;
            const dateLabel = order.status === "zaakceptowane" ? "Planowana data dostawy" : "Faktyczna data dostawy";
            return (
              <div className="ol-card p-4 space-y-3" data-testid="admin-action-card">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" /> {dateLabel}
                  </label>
                  <input
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    data-testid="delivery-date-input"
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Komentarz Office-Land (opcjonalnie)</label>
                  <textarea
                    rows={2}
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    data-testid="admin-comment-input"
                    placeholder="np. dostawca: XYZ Sp. z o.o., numer listu przewozowego…"
                    className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none"
                  />
                </div>
                <button onClick={adminStep} data-testid="admin-next-status"
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold">
                  <AdminIcon className="w-4 h-4" />
                  {ADMIN_NEXT_STATUS[order.status].label}
                </button>
              </div>
            );
          })()}

          {isAdmin && !canAdminStatus && !edit && (
            <div className="ol-card p-4 space-y-3" data-testid="admin-actions-static">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Akcje Office-Land</p>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Komentarz Office-Land</label>
                <textarea
                  rows={3}
                  value={adminComment}
                  onChange={(e) => setAdminComment(e.target.value)}
                  data-testid="admin-comment-static"
                  className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none"
                />
                <button onClick={saveAdminComment} data-testid="save-admin-comment"
                  className="mt-2 w-full px-3 py-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold">
                  Zapisz komentarz
                </button>
              </div>
            </div>
          )}

          {isAdmin && !edit && (
            <div className="flex gap-2">
              <button onClick={() => setEdit(true)} data-testid="admin-edit-order"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-sm font-semibold">
                <Edit3 className="w-4 h-4" /> Edytuj
              </button>
              <button onClick={deleteOrder} data-testid="admin-delete-order"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">
                <Trash className="w-4 h-4" /> Usuń
              </button>
            </div>
          )}

          <Link to="/" className="block text-center text-xs text-slate-500 hover:text-slate-900">Wróć do dashboardu</Link>
        </div>
      </div>
    </div>
  );
}
